import { UseMutationResult } from "@tanstack/react-query";
import { useFormik } from "formik";
import { FinancialAccount } from "@common/types/financialAccount";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";

import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import styles from "./RemoveFinancialAccount.module.scss";

export type AccountAction = "keep-historical-data" | "remove-account";

interface RemoveAccountAttentionForm {
    accountAction: AccountAction;
}

interface RemoveAccountAttentionContentProps {
    account: FinancialAccount;
    closeModal: () => void;
    disableAccount: UseMutationResult<FinancialAccount, unknown, void, unknown>;
    onRemoveAccount: () => void;
    removeAccountLoadingState: boolean;
    accountAction: AccountAction;
    onAccountActionChange: (accountAction: AccountAction) => void;
}

export const RemoveAccountAttentionContent: React.FC<
    RemoveAccountAttentionContentProps
> = ({
    account,
    closeModal,
    disableAccount,
    onRemoveAccount,
    removeAccountLoadingState,
    accountAction,
    onAccountActionChange,
}) => {
    const formik = useFormik<RemoveAccountAttentionForm>({
        initialValues: {
            accountAction,
        },
        onSubmit: (values) => {
            if (values.accountAction === "keep-historical-data") {
                disableAccount.mutate();
                return;
            }
            onRemoveAccount();
        },
    });

    const isKeepingHistoricalData =
        formik.values.accountAction === "keep-historical-data";
    const loading = isKeepingHistoricalData
        ? disableAccount.isPending
        : removeAccountLoadingState;

    return (
        <>
            <StandardModalHeader>
                Would you like to keep the historical data for this account?
            </StandardModalHeader>
            <StandardModalBody>
                <AccountCard account={account} />
                <p className={styles.modalText}>
                    The historical data may contain important information about
                    your transactions, documents, and it may be needed for
                    accounting purposes.
                </p>
                <RadioGroup<AccountAction>
                    name="account-action"
                    aria-label="account-action"
                    value={formik.values.accountAction}
                    onValueChange={(value) => {
                        void formik.setFieldValue("accountAction", value);
                        onAccountActionChange(value);
                    }}
                    className={styles.radioGroup}
                >
                    <Radio<AccountAction>
                        value="keep-historical-data"
                        label="Keep historical data and just disable the account instead"
                    />
                    <Radio<AccountAction>
                        value="remove-account"
                        label="Remove all historical data and account"
                    />
                </RadioGroup>
                <HStack as="footer" gap={12} justify="end">
                    <Button
                        variant="secondary"
                        size="xl"
                        data-testid="confirmation-modal-no-button"
                        onClick={closeModal}
                        disabled={loading}
                    >
                        Back
                    </Button>
                    <ButtonWithLoader
                        variant="primary"
                        onClick={() => {
                            formik.handleSubmit();
                        }}
                        loading={loading}
                        size="xl"
                        data-testid="confirmation-modal-yes-button"
                    >
                        {isKeepingHistoricalData
                            ? "Archive account"
                            : "Remove account"}
                    </ButtonWithLoader>
                </HStack>
            </StandardModalBody>
        </>
    );
};
